import React, { useState } from 'react';
import { useCountry } from '../contexts/CountryContext';
import { MapPin, Check, Globe } from 'lucide-react';

const CountryWelcome = () => {
  const { country, setCountry, countries, showWelcome, dismissWelcome, detectedCountry } = useCountry();
  const [selected, setSelected] = useState(country);

  // Keep the modal selection in sync with the detected country if it arrives
  // after the modal has rendered (server-side geo lookup is async).
  React.useEffect(() => {
    if (detectedCountry && countries[detectedCountry]) {
      setSelected(detectedCountry);
    }
  }, [detectedCountry, countries]);

  // Don't show on auth or admin pages, or on country-explicit routes
  const path = window.location.pathname;
  if (!showWelcome || path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/admin') || path.startsWith('/forgot') || path.startsWith('/agents/') || path.startsWith('/free-valuation') || path.startsWith('/pulse-report')) return null;

  const handleConfirm = () => {
    setCountry(selected);
    dismissWelcome();
  };

  const detected = detectedCountry ? countries[detectedCountry] : null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] animate-fade-in"
        onClick={handleConfirm}
      />

      {/* Welcome Card */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up" data-testid="country-welcome-modal">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-t-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] px-6 pt-8 pb-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-[#E07A3A]/20 rounded-full flex items-center justify-center">
                <Globe className="h-7 w-7 text-[#E07A3A]" />
              </div>
              <h2 className="text-white text-xl font-semibold font-['Playfair_Display'] mb-1">
                Welcome to SHOPPERDEN
              </h2>
              {detected ? (
                <p className="text-white/60 text-sm flex items-center justify-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  We detected you're browsing from {detected.name}
                </p>
              ) : (
                <p className="text-white/60 text-sm">
                  Choose your region for the best experience
                </p>
              )}
            </div>

            {/* Country Options */}
            <div className="px-6 py-6">
              {detected && (
                <div
                  className="mb-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-semibold text-emerald-800"
                  data-testid="auto-detected-badge"
                >
                  <span className="leading-none">
                    <img src={detected.flag} alt={detected.name} className="w-5 h-3.5 object-cover rounded-sm" />
                  </span>
                  <span>Auto-detected: {detected.name}</span>
                  <span className="text-emerald-600/70 font-normal">— tap below to change</span>
                </div>
              )}
              <p className="text-slate-500 text-xs tracking-widest uppercase font-semibold mb-4 text-center">
                Select your market
              </p>
              <div className="space-y-3">
                {Object.values(countries).map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setSelected(c.code)}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 transition-all duration-200 ${
                      selected === c.code
                        ? 'border-[#E07A3A] bg-[#FEF3EC] shadow-sm'
                        : 'border-slate-100 hover:border-slate-200 bg-white'
                    }`}
                    data-testid={`welcome-country-${c.code}`}
                  >
                    <img src={c.flag} alt={c.name} className="w-10 h-7 object-cover rounded shadow-sm" />
                    <div className="flex-1 text-left">
                      <p className={`font-semibold text-sm ${selected === c.code ? 'text-[#E07A3A]' : 'text-slate-800'}`}>
                        {c.name}
                      </p>
                      <p className="text-slate-400 text-xs">
                        Browse in {c.symbol} ({c.currency})
                      </p>
                    </div>
                    {selected === c.code && (
                      <div className="w-6 h-6 bg-[#E07A3A] rounded-full flex items-center justify-center">
                        <Check className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                    {detectedCountry === c.code && selected !== c.code && (
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        Detected
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Confirm Button */}
            <div className="px-6 pb-8">
              <button
                onClick={handleConfirm}
                className="w-full py-4 bg-[#E07A3A] hover:bg-[#C4652E] text-white font-semibold text-sm tracking-widest uppercase rounded-xl transition-colors shadow-lg shadow-[#E07A3A]/20"
                data-testid="welcome-confirm-btn"
              >
                Continue with {countries[selected]?.name}
              </button>
              <p className="text-center text-slate-400 text-xs mt-3">
                You can change this anytime from the flags in the navigation bar
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountryWelcome;
